import { YCBConfigArray } from 'Config/types';

import {
  appsflyer,
  branch,
  facebook,
  help,
  instagram,
  medium,
  onelink,
  policies,
  pressroom,
  tiktok,
  twitter,
  utm,
  youtube,
} from './constants/domain';
import type supportedLanguages from './languages';

export type NavConfig = {
  defaultLocale: keyof typeof supportedLanguages;
  navMenuLinks: {
    ageVerification: string;
    communityResources: string;
    communityGuidelines: string;
    consumerHealthDataPrivacy: string;
    contact: string;
    cookiePolicy: string;
    download: {
      android: string;
      androidDesktop: string;
      hms: string;
      ios: string;
      iosDesktop: string;
      lite: string;
      native: string;
      onelink: string;
    };
    enableJavascript: string;
    facebook: string;
    fbPhoneNumberChange: string;
    faq: {
      home: string;
      locationAccess: string;
      phoneNumberChange: string;
      restorePurchases: string;
      likesYou: string;
      messageBeforeMatch: string;
      passport: string;
      priorityLikes: string;
      privacy: string;
      rewind: string;
      subscriptions: string;
      topPicks: string;
      selfieVerification: string;
      selfieVerificationV2: string;
      ageVerification: string;
      exList: string;
      howWeHandleYourId: string;
      mandatoryLivenessCheck: string;
    };
    genders: string;
    instagram: string;
    intellectualProperty: string;
    jobs: string;
    lbsTerms?: string;
    login: string;
    orientations: string;
    press: string;
    privacy: string;
    privacyAddendum?: string;
    purchaseGiftCard: string;
    safetyAndPolicy: string;
    safetyAndReporting: string;
    inPersonPhysicalHarm: string;
    harassment: string;
    reportingEuropeanUnionIllegalContent: string;
    deleteAccount: string;
    safety: string;
    security: string;
    techBlog: string;
    terminateSubscription?: string;
    terms: string;
    termsInAppPurchases: string;
    tinderSelect: string;
    tiktok: string;
    twitter: string;
    youtube: string;
  };
};

const nav: YCBConfigArray<NavConfig> = [
  {
    settings: ['main'] as const,
    defaultLocale: 'en',
    navMenuLinks: {
      ageVerification: `${help}/hc/articles/360041821872`,
      communityGuidelines: `${policies}/community-guidelines`,
      communityResources: `${policies}/community-resources`,
      consumerHealthDataPrivacy: `${policies}/consumer-health-data-privacy-policy`,
      contact: `${policies}/contact`,
      cookiePolicy: `${policies}/cookie-policy`,
      download: {
        android: `${appsflyer}/9K8a/lst5zcxc`,
        lite: `${appsflyer}/9K8a/2462a775`,
        ios: `${appsflyer}/9K8a/1dj56nts`,
        native: `${appsflyer}/9K8a/ddbsovjl`,
        // https://www.help.tinder.com/hc/en-us/articles/115004647686-Tinder-Overview
        hms: `${branch}/web-hms-download`,
        androidDesktop: `${branch}/web-android-download`,
        iosDesktop: `${branch}/web-ios-download`,
        onelink: `${onelink}/9K8a`,
      },
      enableJavascript: 'https://www.enable-javascript.com',
      facebook: `${facebook}/tinder`,
      fbPhoneNumberChange: `${help}/hc/articles/360005147211`,
      faq: {
        home: `${help}/hc${utm}`,
        locationAccess: `${help}/hc/articles/115005668326-I-denied-Tinder-access-to-my-location-${utm}`,
        phoneNumberChange: `${help}/hc/articles/360005147211${utm}`,
        restorePurchases: `${help}/hc/articles/115004478863-How-do-I-restore-my-purchase-${utm}`,
        // Premium Features
        likesYou: `${help}/hc/articles/115005246123-Likes-You${utm}`,
        messageBeforeMatch: `${help}/hc/articles/360046358932-Message-Before-Match-${utm}`,
        passport: `${help}/hc/articles/115004490423-Passport${utm}`,
        priorityLikes: `${help}/hc/articles/360046802931-Priority-Likes${utm}`,
        privacy: `${help}/hc/articles/5349453268877-Privacy-FAQs${utm}`,
        rewind: `${help}/hc/articles/115004493323${utm}`,
        subscriptions: `${help}/hc/articles/115004487406-Tinder-Subscriptions${utm}`,
        topPicks: `${help}/hc/articles/360005039092${utm}`,
        // Trust
        selfieVerification: `${help}/hc/articles/360034941812-What-is-Photo-Verification-${utm}`,
        selfieVerificationV2: `${help}/hc/articles/4422771431309${utm}`,
        ageVerification: `${help}/hc/articles/360040592771-How-does-age-verification-work-${utm}`,
        exList: `${help}/hc/articles/360039684672-Block-Contacts-${utm}`,
        howWeHandleYourId: `${help}/hc/articles/4403779020941${utm}`,
        mandatoryLivenessCheck: `${policies}/faq-mandatory-liveness-check`,
      },
      genders: `${pressroom}/genders`,
      instagram: `${instagram}/tinder`,
      intellectualProperty: `${policies}/intellectual-property`,
      jobs: 'https://www.lifeattinder.com',
      login: 'https://tinder.onelink.me/9K8a/3d4abb81', // points to /app/login
      orientations: `${pressroom}/orientations`,
      press: pressroom,
      privacy: `${policies}/privacy`,
      purchaseGiftCard: 'https://tinder.cashstar.com/',
      safetyAndPolicy: `${policies}/safety-and-policy`,
      safetyAndReporting: `${help}/hc/categories/360006058312-Safety-Reporting${utm}`,
      inPersonPhysicalHarm: `${help}/hc/en-us/articles/115003822023-Reporting-in-person-physical-harm${utm}`,
      harassment: `${help}/hc/en-us/requests/new?ticket_form_id=360000234472`,
      reportingEuropeanUnionIllegalContent: `${help}/hc/articles/19530947945869${utm}`,
      deleteAccount: `${help}/hc/articles/6956972185229`,
      safety: `${policies}/safety`,
      security: `${policies}/security`,
      techBlog: `${medium}/tinder-engineering`,
      terms: `${policies}/terms`,
      termsInAppPurchases: `${policies}/terms#purchase`,
      tiktok: `${tiktok}/@tinder`,
      tinderSelect: 'https://www.help.tinder.com/hc/articles/15408075254797',
      twitter: `${twitter}/Tinder`,
      youtube: `${youtube}/Tinder`,
    },
  },
  // South America
  {
    settings: ['country:BR'],
    defaultLocale: 'pt',
    navMenuLinks: {
      press: 'https://br.tinderpressroom.com/',
      // social
      instagram: `${instagram}/tinderbrasil`,
      twitter: `${twitter}/tinderbrasil`,
      tiktok: `${tiktok}/@tinder.brasil`,
      youtube: `${youtube}/channel/UCGcSuYNcsSb_YJV53U5558A`,
      genders: 'https://br.tinderpressroom.com/genders',
      orientations: 'https://br.tinderpressroom.com/orientations',
    },
  },
  // Central America
  {
    settings: ['country:MX'],
    defaultLocale: 'es',
    navMenuLinks: {
      // social
      instagram: `${instagram}/tindermexico`,
      youtube: `${youtube}/channel/UCiW2pZrAs2yozaZ4DzgXwcg`,
    },
  },
  // Asia
  {
    settings: ['country:KR'],
    defaultLocale: 'ko',
    navMenuLinks: {
      press: 'https://kr.tinderpressroom.com/',
      lbsTerms: `${policies}/sko-lbs-terms`,
      privacyAddendum: `${policies}/sko-privacy-addendum`,
      // social
      instagram: `${instagram}/tinderkorea`,
      twitter: `${twitter}/tinderkorea`,
      tiktok: `${tiktok}/@tinderkorea`,
      youtube: `${youtube}/TinderKoreaGo`,
      genders: 'https://kr.tinderpressroom.com/genders',
      orientations: 'https://kr.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:JP'],
    defaultLocale: 'ja',
    navMenuLinks: {
      press: 'https://jp.tinderpressroom.com/',
      // social
      facebook: `${facebook}/TinderJP`,
      instagram: `${instagram}/tinder_japan_official`,
      twitter: `${twitter}/Tinder_Japan`,
      youtube: `${youtube}/channel/UCFpedMK71PeAAtmO4CEGylw`,
    },
  },
  {
    settings: ['country:TW'],
    defaultLocale: 'zh-Hant',
    navMenuLinks: {
      press: 'https://tw.tinderpressroom.com/',
      // social
      facebook: `${facebook}/tindertaiwanofficial`,
      instagram: `${instagram}/tindertaiwan`,
      youtube: `${youtube}/TinderTaiwanOfficial`,
    },
  },
  {
    settings: ['country:South Asia'],
    navMenuLinks: {
      press: 'https://in.tinderpressroom.com/',
      facebook: `${facebook}/TinderInd`,
      instagram: `${instagram}/tinder_india`,
      twitter: `${twitter}/Tinder_India`,
      youtube: `${youtube}/TinderIndia`,
    },
  },
  // Southeast Asia
  {
    settings: ['country:TH'],
    defaultLocale: 'th',
    navMenuLinks: {
      press: 'https://th.tinderpressroom.com/',
      // social
      facebook: `${facebook}/TinderThailand`,
      instagram: `${instagram}/tinder_thailand`,
      twitter: `${twitter}/Tinderthailand`,
    },
  },
  {
    settings: ['country:ID'],
    defaultLocale: 'id',
    navMenuLinks: {
      press: 'https://id.tinderpressroom.com/',
      facebook: `${facebook}/Tinder-Indonesia-107563930984669/`,
      instagram: `${instagram}/tinderindonesia`,
      tiktok: `${tiktok}/@tinderindonesia`,
      twitter: `${twitter}/TinderIndonesia`,
      youtube: `${youtube}/channel/UCnplK_NE54gkEt0VGrUDg1g`,
    },
  },
  {
    settings: ['country:VN'],
    defaultLocale: 'vi',
    navMenuLinks: {
      press: 'https://vn.tinderpressroom.com/',
      facebook: `${facebook}/Tinder-Vietnam-106524054422640`,
      instagram: `${instagram}/tinder_vietnam`,
      youtube: `${youtube}/@tindervietnam1216`,
    },
  },
  {
    settings: ['country:PH'],
    defaultLocale: 'tl',
    navMenuLinks: {
      facebook: `${facebook}/Tinder-Philippines-105080451753399`,
      instagram: `${instagram}/tinderphilippines`,
    },
  },
  {
    settings: ['country:SG'],
    navMenuLinks: {
      press: 'https://sg.tinderpressroom.com/',
      genders: 'https://sg.tinderpressroom.com/genders',
      orientations: 'https://sg.tinderpressroom.com/orientations',
      tiktok: `${tiktok}/@tindersignapore`,
    },
  },
  {
    settings: ['country:Southeast Asia'],
    navMenuLinks: {
      facebook: `${facebook}/TinderSEAsia`,
      youtube: `${youtube}/channel/UCnplK_NE54gkEt0VGrUDg1g`,
    },
  },
  // Europe
  {
    settings: ['country:FR'],
    defaultLocale: 'fr',
    navMenuLinks: {
      press: 'https://fr.tinderpressroom.com/',
      // social
      facebook: `${facebook}/tinderfr`,
      instagram: `${instagram}/tinderfr`,
      tiktok: `${tiktok}/@tinder.france`,
      twitter: `${twitter}/tinderfrance`,
      youtube: `${youtube}/@tinder_france`,
      genders: 'https://fr.tinderpressroom.com/genders',
      orientations: 'https://fr.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:DE'],
    defaultLocale: 'de',
    navMenuLinks: {
      press: 'https://de.tinderpressroom.com/',
      terminateSubscription: `${help}/hc/requests/new?ticket_form_id=360000234452&tf_360013981632=f_cancel_request`,
      // social
      facebook: `${facebook}/tindergermany`,
      instagram: `${instagram}/tinderde`,
      tiktok: `${tiktok}/@tinder.germany`,
      youtube: `${youtube}/@tinder_germany`,
    },
  },
  {
    settings: ['country:ES'],
    defaultLocale: 'es-ES',
    navMenuLinks: {
      press: 'https://es.tinderpressroom.com/',
      // social
      facebook: `${facebook}/TinderESP`,
      instagram: `${instagram}/tinderes`,
      twitter: `${twitter}/tinder_spain`,
      youtube: `${youtube}/@tinder_espana`,
      genders: 'https://es.tinderpressroom.com/genders',
      orientations: 'https://es.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:GB,IE'],
    defaultLocale: 'en-GB',
    navMenuLinks: {
      press: 'https://uk.tinderpressroom.com/',
      // social
      facebook: `${facebook}/TinderUK`,
      instagram: `${instagram}/tinderuk`,
      tiktok: `${tiktok}/@tinder_uk`,
      twitter: `${twitter}/TinderUk`,
      youtube: `${youtube}/@tinder_uk`,
      genders: 'https://uk.tinderpressroom.com/genders',
      orientations: 'https://uk.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:DK'],
    defaultLocale: 'da',
    navMenuLinks: {
      terms: `${policies}/terms/dk/da`,
      youtube: `${youtube}/channel/UC-MbW9HpBmYuTQipmIQLJdg`,
    },
  },
  {
    settings: ['country:IT'],
    defaultLocale: 'it',
    navMenuLinks: {
      instagram: `${instagram}/tinderit`,
      twitter: `${twitter}/TinderIt`,
      press: 'https://it.tinderpressroom.com/',
      genders: 'https://it.tinderpressroom.com/genders',
      orientations: 'https://it.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:NO'],
    defaultLocale: 'nb',
    navMenuLinks: {
      press: 'https://se.tinderpressroom.com/',
      genders: 'https://se.tinderpressroom.com/genders',
      orientations: 'https://se.tinderpressroom.com/orientations',
    },
  },
  {
    settings: ['country:NL'],
    defaultLocale: 'nl',
    navMenuLinks: {
      press: 'https://nl.tinderpressroom.com/',
      // social
      // instagram: `${instagram}/tindernl`, not claimed yet
      twitter: `${twitter}/TinderNL`,
    },
  },
  // Oceania
  {
    settings: ['country:AU'],
    defaultLocale: 'en-AU',
    navMenuLinks: {
      press: 'https://au.tinderpressroom.com/',
      facebook: `${facebook}/TinderAustralia`,
      instagram: `${instagram}/tinderau`,
      tiktok: `${tiktok}/@tinderau`,
      youtube: `${youtube}/@tinderaustralia`,
      genders: 'https://au.tinderpressroom.com/genders',
      orientations: 'https://au.tinderpressroom.com/orientations',
    },
  },
  // Tinder Lite
  {
    settings: ['lite:1'],
    navMenuLinks: {
      // TODO: potentially update with AppsFlyer link
      download: {
        lite: `${branch}/lite-android-download`,
      },
    },
  },
];

export default nav;
